import { addFooter, headerReport } from "../../reports/configs/utils";
import jsPDF from "jspdf";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

const defaultConfigStylesTable = {
    fontSize: 9,
    overflow: 'linebreak',
    cellPadding: 2
};

const marginLeft = 40;
const marginSecondColumn = 440;
const unit = "pt";
const size = "A4";
const orientation = "landscape";

export function exportGHEsToPDF({ tableData, selectedEmpresa, dadosUsuario, reportName, reportTitle }) {
    const doc = new jsPDF(orientation, unit, size);

    // Cabeçalho do relatório (logo + infos da empresa)
    const strokeWidth = reportTitle.length * 8.3;
    headerReport(doc, selectedEmpresa, reportTitle, strokeWidth);

    let startY = selectedEmpresa ? 105 : 90;

    tableData.forEach((grupo) => {
        // === Grupo (cabeçalho em verde) ===
        doc.autoTable({
            startY,
            head: [['Grupo (GHE)', 'Cód. Externo', 'Descrição', 'Observação', 'Status']],
            body: [[
                grupo.nome || '-',
                grupo.cod_externo || '-',
                grupo.descricao || '-',
                grupo.observacao || '-',
                grupo.status ? 'Ativo' : 'Inativo'
            ]],
            theme: 'grid',
            headStyles: { fillColor: [30, 81, 57], textColor: 255, fontStyle: 'bold' }, // Verde escuro
            styles: defaultConfigStylesTable,
            didDrawPage: (data) => addFooter(doc, data, dadosUsuario.pessoa)
        });

        startY = doc.lastAutoTable.finalY + 2;

        // === Categorias do grupo ===
        if (grupo.categorias && grupo.categorias.length > 0) {
            grupo.categorias.forEach((cat) => {
                doc.autoTable({
                    startY,
                    head: [['Categoria', 'Quantidade Máxima', 'Período', 'Ponto de Alerta', 'Status']],
                    body: [[
                        cat.nome || '-',
                        cat.quantidade_maxima || '-',
                        cat.periodo || '-',
                        cat.ponto_alerta || '-',
                        cat.status ? 'Ativo' : 'Inativo'
                    ]],
                    theme: 'grid',
                    headStyles: { fillColor: [150, 150, 150], fontStyle: 'bold' }, // cinza claro
                    styles: { fontSize: 9 },
                    didDrawPage: (data) => addFooter(doc, data, dadosUsuario.pessoa)
                });

                startY = doc.lastAutoTable.finalY + 1;

                // === Permissões da categoria ===
                if (cat.permissoes && cat.permissoes.length > 0) {
                    doc.autoTable({
                        startY,
                        head: [['Produto', 'Período', 'Ponto de Alerta', 'Status', 'Sobrescreve Categoria']],
                        body: cat.permissoes.map((perm) => [
                            [
                                perm.produto?.nome || '-',
                                perm.produto?.cod_externo_filhos?.length
                                    ? `(${perm.produto.cod_externo_filhos.join(', ')})`
                                    : ''
                            ].join(' '),
                            perm.periodo || '-',
                            perm.ponto_alerta || '-',
                            perm.status ? 'Ativo' : 'Inativo',
                            perm.info_categoria ? 'Não' : 'Sim'
                        ]),
                        theme: 'grid',
                        headStyles: { fillColor: [180, 180, 180], fontStyle: 'bold' }, // mais clarinho
                        styles: { fontSize: 8, cellPadding: 2 },
                        didDrawPage: (data) => addFooter(doc, data, dadosUsuario.pessoa)
                    });

                    startY = doc.lastAutoTable.finalY + 1;
                }

                const pageHeight = doc.internal.pageSize.height;

                if (startY > pageHeight - 40) { // deixa uma margem de 40pt
                    doc.addPage();
                    startY = 40; // reinicia a posição no topo, mas com margem
                }
            });
        }

        startY += 4; // pequeno espaço antes do próximo grupo
    });

    doc.save(`${reportName}_${dayjs().format('YYYY-MM-DD_HHmm')}.pdf`);
}

export function exportGHEsToExcel({ tableData, reportName }) {
    const rows = [];

    tableData.forEach((grupo) => {
        rows.push(["Grupo (GHE)", "Cód. Externo", "Descrição", "Observação", "Status"]);
        rows.push([
            grupo.nome || "-",
            grupo.cod_externo || "-",
            grupo.descricao || "-",
            grupo.observacao || "-",
            grupo.status ? "Ativo" : "Inativo"
        ]);

        if (grupo.categorias && grupo.categorias.length > 0) {
            grupo.categorias.forEach((cat) => {
                rows.push(["Categoria", "Quantidade Máxima", "Período", "Ponto de Alerta", "Status"]);
                rows.push([
                    cat.nome || "-",
                    cat.quantidade_maxima || "-",
                    cat.periodo || "-",
                    cat.ponto_alerta || "-",
                    cat.status ? "Ativo" : "Inativo"
                ]);

                if (cat.permissoes && cat.permissoes.length > 0) {
                    rows.push(["Produto", "Período", "Ponto de Alerta", "Status", "Sobrescreve Categoria"]);
                    cat.permissoes.forEach((perm) => {
                        rows.push([
                            [
                                perm.produto?.nome || '-',
                                perm.produto?.cod_externo_filhos?.length
                                    ? `(${perm.produto.cod_externo_filhos.join(', ')})`
                                    : ''
                            ].join(' '),
                            perm.periodo || "-",
                            perm.ponto_alerta || "-",
                            perm.status ? "Ativo" : "Inativo",
                            perm.info_categoria ? "Não" : "Sim"
                        ]);
                    });
                }
            });
        }

        rows.push([]); // linha em branco entre grupos
    });

    const worksheet = XLSX.utils.aoa_to_sheet(rows);

    // Ajusta largura das colunas (px aproximado)
    worksheet["!cols"] = [
        { wch: 25 }, // Grupo / Categoria / Produto
        { wch: 15 }, // Cod. Externo / Período
        { wch: 20 }, // Descrição
        { wch: 20 }, // Observação / Ponto de alerta
        { wch: 15 }  // Status
    ];

    // Aplica estilo no cabeçalho (básico, depende do viewer)
    Object.keys(worksheet).forEach((cell) => {
        if (cell[0] === "A" && worksheet[cell].v === "Grupo (GHE)") {
            worksheet[cell].s = { fill: { fgColor: { rgb: "1E5139" } }, font: { color: { rgb: "FFFFFF" }, bold: true } };
        }
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Relatório");

    XLSX.writeFile(workbook, `${reportName}_${dayjs().format("YYYY-MM-DD_HHmm")}.xlsx`);
}

// === CSV (UTF-8 com BOM) ===
export function exportGHEsToCSV({ tableData, reportName }) {
    const rows = [];

    tableData.forEach((grupo) => {
        rows.push(["Grupo (GHE)", "Cód. Externo", "Descrição", "Observação", "Status"]);
        rows.push([
            grupo.nome || "-",
            grupo.cod_externo || "-",
            grupo.descricao || "-",
            grupo.observacao || "-",
            grupo.status ? "Ativo" : "Inativo"
        ]);

        if (grupo.categorias && grupo.categorias.length > 0) {
            grupo.categorias.forEach((cat) => {
                rows.push(["Categoria", "Quantidade Máxima", "Período", "Ponto de Alerta", "Status"]);
                rows.push([
                    cat.nome || "-",
                    cat.quantidade_maxima || "-",
                    cat.periodo || "-",
                    cat.ponto_alerta || "-",
                    cat.status ? "Ativo" : "Inativo"
                ]);

                if (cat.permissoes && cat.permissoes.length > 0) {
                    rows.push(["Produto", "Período", "Ponto de Alerta", "Status", "Sobrescreve Categoria"]);
                    cat.permissoes.forEach((perm) => {
                        rows.push([
                            [
                                perm.produto?.nome || '-',
                                perm.produto?.cod_externo_filhos?.length
                                    ? `(${perm.produto.cod_externo_filhos.join(', ')})`
                                    : ''
                            ].join(' '),
                            perm.periodo || "-",
                            perm.ponto_alerta || "-",
                            perm.status ? "Ativo" : "Inativo",
                            perm.info_categoria ? "Não" : "Sim"
                        ]);
                    });
                }
            });
        }

        rows.push([]);
    });

    // Monta CSV manualmente
    const csv = rows
        .map(row => row.map(cell => {
            const value = cell != null ? String(cell) : "";
            return `"${value.replace(/"/g, '""')}"`;
        }).join(";"))
        .join("\n");

    // Adiciona BOM para Excel abrir como UTF-8
    const csvWithBOM = "\uFEFF" + csv;

    const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${reportName}_${dayjs().format("YYYY-MM-DD_HHmm")}.csv`;
    link.click();
}